import { useEffect } from 'react'
import { logger } from './logger'

// Per-tab, so every window holds its own lock. The name used to be a constant,
// which looked right but silently protected only one window: Web Locks are
// scoped per origin and shared across tabs, and the default mode is exclusive,
// so with three windows open one held the lock and the other two queued behind
// it forever — free to be frozen, and to defer their Firestore listeners until
// the operator clicked back into them.
const TAB_KEEPALIVE_LOCK = `cleverlohn-notification-keepalive-${
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
}`

/**
 * Prevents the browser from freezing/discarding this tab by holding a Web Lock.
 *
 * Chrome's Memory Saver and Tab Freezing features can suspend background tabs
 * after ~5 minutes, killing Firestore listeners and preventing notifications.
 * Holding a Web Lock signals to the browser that the tab is doing important
 * background work and should not be frozen.
 *
 * Every window staying awake does not mean every window rings: the approval
 * alert is claimed per run across tabs (see claimApprovalAlert).
 *
 * For extra reliability, users can also add the site to Chrome's
 * "Always keep these sites active" list under chrome://settings/performance.
 */
export function useTabKeepAlive(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return
    if (!('locks' in navigator)) {
      logger.info('Web Locks API not supported — tab keep-alive unavailable')
      return
    }

    const controller = new AbortController()

    navigator.locks
      .request(TAB_KEEPALIVE_LOCK, { signal: controller.signal }, () => {
        logger.debug('Tab keep-alive lock acquired')
        return new Promise<void>(() => {
          // Never resolves — holds the lock until the component unmounts
          // and the AbortController fires.
        })
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return
        logger.warn('Failed to acquire tab keep-alive lock', {
          error: String(err),
        })
      })

    return () => {
      controller.abort()
    }
  }, [enabled])
}
